import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";

import TruncatedWithTooltip from "../../../../components/TruncatedWithTooltip";
import { UIAvatarStack, UIStatusTag } from "../../../../components/ui";
import { formatDate } from "../../../../utils/Helpers";

export const nameByLang = (entity, lang) =>
  entity?.[`name_${lang}`] || entity?.name || "";

export const companyName = (company, lang) => nameByLang(company, lang) || "-";

const DESIGN_COLUMN_WIDTH = 177;
// Fits "#EVT-123456" in full; longer ids truncate with a tooltip.
const EVENT_ID_COLUMN_WIDTH = 140;

const fixedColumn = (width = DESIGN_COLUMN_WIDTH) => ({
  width,
  minWidth: width,
  maxWidth: width,
  flex: undefined,
});

const rowVenue = (row) => row.address || "";

const rowEventType = (row, lang) =>
  row.event_type_label ||
  row.event_type?.[`name_${lang}`] ||
  row.event_type?.name ||
  row.event_type ||
  row.payment_period ||
  "-";

const EventNameCell = ({ name }) => {
  const value = name || "-";

  if (value === "-") {
    return <span className="MuiDataGrid-cellContent">-</span>;
  }

  return (
    <TruncatedWithTooltip title={value}>
      <span className="MuiDataGrid-cellContent">{value}</span>
    </TruncatedWithTooltip>
  );
};

EventNameCell.propTypes = {
  name: PropTypes.string,
};

EventNameCell.defaultProps = {
  name: "",
};

const formatEventId = (row, labels) => {
  const id = row.id;
  return id ? `#EVT-${String(id).padStart(4, "0")}` : labels.notAvailable;
};

const StaffingFillCell = ({ accepted, required }) => {
  const percent = required > 0 ? Math.min((accepted / required) * 100, 100) : 0;
  const complete = required > 0 && accepted >= required;

  return (
    <Box className="events-list-view__staffing-fill">
      <Typography
        component="span"
        className="events-list-view__staffing-count text-label-sm text-primary-900"
      >
        {accepted}/{required}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        className="events-list-view__staffing-progress"
        sx={{
          "& .MuiLinearProgress-bar": {
            backgroundColor: complete
              ? "var(--color-success-default)"
              : "var(--color-warning-500)",
          },
        }}
      />
    </Box>
  );
};

StaffingFillCell.propTypes = {
  accepted: PropTypes.number.isRequired,
  required: PropTypes.number.isRequired,
};

export function useEventsListColumns(labels, lang) {
  return useMemo(() => {
    return [
      {
        field: "eventCode",
        headerName: labels.eventId,
        ...fixedColumn(EVENT_ID_COLUMN_WIDTH),
        sortable: false,
        renderCell: ({ row }) => {
          const eventId = formatEventId(row, labels);
          return (
            <TruncatedWithTooltip title={eventId}>
              <span className="MuiDataGrid-cellContent events-list-view__event-id">
                {eventId}
              </span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "name",
        headerName: labels.eventName,
        ...fixedColumn(),
        sortable: false,
        renderCell: ({ row }) => <EventNameCell name={row.name} />,
      },
      {
        field: "company",
        headerName: labels.owningCompany,
        ...fixedColumn(),
        sortable: false,
        // Plain text: the whole row opens the event details.
        renderCell: ({ row }) => {
          const name = companyName(row._company, lang);
          return (
            <TruncatedWithTooltip title={name}>
              <span className="MuiDataGrid-cellContent">{name}</span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "eventType",
        headerName: labels.eventType,
        ...fixedColumn(),
        sortable: false,
        renderCell: ({ row }) => {
          const value = rowEventType(row, lang);
          return (
            <TruncatedWithTooltip title={value}>
              <span className="MuiDataGrid-cellContent">{value}</span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "event_dates",
        headerName: labels.startEndDate,
        ...fixedColumn(),
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0} className="events-list-view__date-range">
            <span>{formatDate(row.event_start_at)}</span>
            <span>{formatDate(row.event_end_at)}</span>
          </Stack>
        ),
      },
      {
        field: "location",
        headerName: labels.locationVenue,
        ...fixedColumn(),
        sortable: false,
        renderCell: ({ row }) => {
          // Events have no city of their own — only a venue/address. Showing the
          // owning company's city here was misleading: it never reflects the
          // event's actual (and editable) location.
          const venue = rowVenue(row) || "-";

          return (
            <TruncatedWithTooltip title={venue}>
              <span className="MuiDataGrid-cellContent">{venue}</span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "staffingFill",
        headerName: labels.staffingFill,
        ...fixedColumn(),
        sortable: false,
        renderCell: ({ row }) => (
          <Box className="events-list-view__cell-content">
            <StaffingFillCell
              accepted={row._acceptedStaff}
              required={row._requiredStaff}
            />
          </Box>
        ),
      },
    ];
  }, [labels, lang]);
}

export function usePayrollEventsListColumns(listLabels, creatEventLabels) {
  return useMemo(
    () => [
      {
        field: "name",
        headerName: listLabels.eventsColumn,
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => <EventNameCell name={row.name} />,
      },
      {
        field: "project_managers",
        headerName: listLabels.projectManagers,
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <UIAvatarStack
            items={(row.project_managers || []).map((manager) => ({
              id: manager.admin?.id ?? manager.id,
              name: manager.admin?.name,
              image: manager.admin?.image_url,
            }))}
          />
        ),
      },
      {
        field: "payment_period",
        headerName: listLabels.type,
        flex: 0.75,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <div className="MuiDataGrid-cellContent">
            {row.payment_period ? (
              <UIStatusTag
                preset="table"
                status={row.payment_period}
                label={
                  listLabels[row.payment_period] ?? row.payment_period
                }
              />
            ) : (
              "-"
            )}
          </div>
        ),
      },
      {
        field: "event_start_at",
        headerName: listLabels.startDate,
        flex: 0.8,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent mobile-style">
            {row.event_start_at ? formatDate(row.event_start_at) : "-"}
          </span>
        ),
      },
      {
        field: "event_end_at",
        headerName: listLabels.endDate,
        flex: 0.8,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent mobile-style">
            {row.event_end_at ? formatDate(row.event_end_at) : "-"}
          </span>
        ),
      },
      {
        field: "publish_mode",
        headerName: listLabels.publishMode,
        flex: 0.9,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent events-list-view__publish-mode">
            {row.is_private
              ? creatEventLabels.private
              : creatEventLabels.public}
          </span>
        ),
      },
    ],
    [creatEventLabels, listLabels],
  );
}
